import { Router } from "@angular/router";

import { from, throwError } from "rxjs";

import { finalize, switchMap, catchError, filter, take } from "rxjs/operators";
import { Injectable } from "@angular/core";
import {
  HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor,
  HttpRequest,
  HttpXsrfTokenExtractor
} from "@angular/common/http";
import { Observable, BehaviorSubject } from "rxjs";
import { OAuthClient } from "./oauth.client";
import { TranslateService } from "@ngx-translate/core";
import { AuthClient } from "./auth.client";
import { Accounts2ClientService } from "./accounts2.client";
import { I18nService } from "i18n/i18n.service";
import { ShowToastService } from "app/shared/services/showToast.service";

@Injectable()
export class RestApiInterceptorService implements HttpInterceptor {
  isRefreshingToken = false;
  tokenSubject: BehaviorSubject<string> = new BehaviorSubject<string>(null);

  constructor(private oAuthClient: OAuthClient,
    private _authClient: AuthClient,
    private _translateService: TranslateService,
    private accounts2Service: Accounts2ClientService,
    private _router: Router,
    private _tokenExtractor: HttpXsrfTokenExtractor,
    private _i18nService: I18nService,
    private _showToastService: ShowToastService) {
  }

  public intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const headerName = 'X-XSRF-TOKEN';
    const token = this._tokenExtractor.getToken() as string;
    if (token !== null && !request.headers.has(headerName) && !request.headers.has("Authorization")) {
      request = request.clone({ headers: request.headers.set(headerName, token) });
    }
    this._authClient.sessionUpdate.next();
    return next.handle(request).pipe(catchError(error => {
      this._authClient.unsubscribeTimer();
      if (error instanceof HttpErrorResponse) {
        switch ((error as HttpErrorResponse).status) {
          case 400:
            if (error.error && error.error.Message) {
              this._translateService.get(error.error.Message).subscribe((message: string) => {
                this._showToastService.showToast(message, 'error');
              });
            } else {
              this._showToastService.showToast(this._i18nService.instant('GLOBAL.AN_ERROR_HAS_OCCURRED'), 'error');
            }
            break;
          case 401:
            if (this._authClient.authType === "OpenId") {
              this._authClient.logout();
              return;
            } else {
              return this.handle401Error(request, next);
            }

          case 403:
            this._router.navigate(["mc", "Forbidden"]);
            return next.handle(request);

          case 406:
            this.oAuthClient.logout();
            break;

          case 404:
            let decoder = new TextDecoder('utf-8');
            let responseString = decoder.decode(new Uint8Array(error.error));
            if (responseString === '"FileNotFound"') {
              this._showToastService.showToast(this._i18nService.instant('GLOBAL.FILE_NOT_FOUND'), 'error');
            } else {
              this._showToastService.showToast(this._i18nService.instant('GLOBAL.MESSAGE_ALERT_404'), 'error');
            }
            break;

          case 0:
            const wsfFormLogout: HTMLFormElement = document.getElementById("wsfLogout") as HTMLFormElement;
            wsfFormLogout.submit();
            break;

          case 415:
            this._showToastService.showToast(this._i18nService.instant('GLOBAL.INVALID_FILE'), 'error');
            return next.handle(request);

          case 413:
            this._showToastService.showToast('The request filtering module is configured to deny a request that exceeds the request content length.', 'error');
            return next.handle(request);

          default:
            const messageToTranslate = this._getTranslateErrorKey(request.url, error);
            this._translateService
              .get(messageToTranslate)
              .subscribe((message: string) => {
                if (message.includes("{error}")) {
                  message = message.replace("{error}", error.error.Message);
                }
                this._showToastService.showToast(message, 'error');
              });
            return next.handle(request);
        }
      }
    }));
  }

  handle401Error(req: HttpRequest<any>, next: HttpHandler) {
    if (!this.isRefreshingToken) {
      this.isRefreshingToken = true;
      this.tokenSubject.next(null);

      const refreshPromise = this.GetRefreshPromise();

      if (refreshPromise) {
        const source = from(refreshPromise);

        return source.pipe(switchMap((newToken: any) => {
          if (newToken) {
            this.tokenSubject.next(newToken);
            if (this.isAutoLoginAuth()) {
              return next.handle(this._addAutoLoginToken(req, newToken));
            }
            return next.handle(this._addToken(req, typeof newToken === "string" ? newToken : newToken.access_token));
          }
        }), catchError(error => {
          if (this.accounts2Service.getAuthenticationConfig().isAccounts2) {
            this.accounts2Service.getInstance().then(instance => instance.authorize());
          } else if (this.isAutoLoginAuth()) {
            this.isRefreshingToken = false;
            this._authClient.logout();
            return;
          } else {
            return throwError(error);
          }
        }), finalize(() => {
          this.isRefreshingToken = false;
        }));
      }
    } else {
      return this.tokenSubject.pipe(
        filter(token => token != null),
        take(1),
        switchMap(token => {
          if (this.isAutoLoginAuth()) {
            return next.handle(this._addAutoLoginToken(req, token));
          }
          return next.handle(req);
        }));
    }
  }

  private isAutoLoginAuth(): boolean {
    return this._authClient.authType === "AutoLogin";
  }

  private GetRefreshPromise() {
    let refreshPromise;
    if (this.accounts2Service.getAuthenticationConfig().isAccounts2) {
      refreshPromise = new Promise(async (resolve, reject) => {
        const result = await (await this.accounts2Service.getInstance()).refreshToken();
        if (result) {
          resolve((await this.accounts2Service.getInstance()).getToken());
        } else {
          reject("Not token");
        }
      });
    } else if (this.isAutoLoginAuth()) {
      refreshPromise = this._authClient.refreshToken().toPromise();
    }
    else {
      refreshPromise = this.oAuthClient.refreshToken();
    }
    return refreshPromise;
  }

  private _addToken(req: HttpRequest<any>, token: string): HttpRequest<any> {
    return req.clone({ setHeaders: { Authorization: "Bearer " + token } });
  }

  private _addAutoLoginToken(req: HttpRequest<any>, token: string): HttpRequest<any> {
    return req.clone({ setHeaders: { Authorization: token } });
  }

  private _getTranslateErrorKey(endpointUrl: string, error: any) {
    const url = endpointUrl.toLowerCase();
    switch (true) {
      case url.indexOf("authentication") !== -1:
        return "AUTHENTICATION.ERROR_" + error.error.Message.toUpperCase();

      case url.indexOf("bots") !== -1:
        return "RPA_BOTS." + error.error.Message.toUpperCase();

      case url.indexOf("maintenancewindowreasons") !== -1:
        return "BEX: " + error.error.Message.toUpperCase();

      case url.indexOf("bex/import") !== -1 || url.indexOf("bex/getcurrentstatus") !== -1:
        return "Import error: " + error.error.Message.toUpperCase();

      case url.indexOf("import/execute-import") !== -1:
        return error.error.Message.includes("Invalid object name") ? "SYNC_FILES.MISSING_IMPORTS" : "GLOBAL.AN_ERROR_HAS_OCCURRED";

      case url.indexOf("Encryption") !== -1:
        return error.error.Message;

      case url.indexOf("baseknowledgecatalog/deletekbcatalog") !== -1:
        return error.error.Message;

      default:
        return "GLOBAL.AN_ERROR_HAS_OCCURRED";
    }
  }

}
